import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient } from '../lib/api';
import type { User } from '../lib/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  console.log('AuthProvider rendering, user:', user, 'isLoading:', isLoading);

  const isAuthenticated = !!user;

  const login = async (email: string, password: string) => {
    console.log('🔑 AuthContext login called for:', email);
    try {
      console.log('📡 Calling apiClient.login...');
      const response = await apiClient.login({ email, password });
      console.log('🎉 Login response received:', response);
      
      apiClient.setToken(response.token);
      setUser(response.user);
      console.log('✅ User set in AuthContext:', response.user);
    } catch (error) {
      console.error('💥 Login error in AuthContext:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      apiClient.clearToken();
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await apiClient.getUserProfile();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      apiClient.clearToken();
      setUser(null);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      console.log('Initializing auth...');
      const token = localStorage.getItem('admin_token');
      console.log('Found token:', !!token);
      
      if (token) {
        try {
          console.log('Attempting to refresh user with token...');
          await refreshUser();
          console.log('User refresh successful');
        } catch (error) {
          console.error('Initial auth check failed:', error);
          // Clear invalid token
          localStorage.removeItem('admin_token');
          setUser(null);
        }
      } else {
        console.log('No token found, user remains null');
      }
      
      console.log('Auth initialization complete, setting isLoading to false');
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};